import { CloudOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOnlineStatus } from "@/offline/online-status";
import { useOfflineChipClaimed } from "./offline-chip-claim";

/**
 * Floating pill that surfaces offline state across every screen. Mounted once
 * in `__root.tsx` so each page layout doesn't have to remember it. Positioned
 * `fixed` near the top of the viewport, horizontally centered, below the
 * sticky header / search bar of every current screen.
 *
 * The z-index sits ABOVE sticky headers (which use z-40) so the chip stays
 * visible regardless of scroll position, but BELOW dialogs/sheets (which use
 * z-50+) so modals still feel layered.
 *
 * The element stays mounted in both states so the entry/exit fade animation
 * runs; `aria-hidden` flips with state so screen readers only see the chip
 * when it's communicating something.
 */
export function OfflineChip() {
  const isOnline = useOnlineStatus();
  const claimed = useOfflineChipClaimed();
  // While another surface (e.g. ProjectHeader) owns the offline indicator,
  // suppress the floating chip so we don't render two pills at once.
  const hidden = isOnline || claimed;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-hidden={hidden}
      className={cn(
        "pointer-events-none fixed left-1/2 z-[45] flex items-center gap-1.5",
        "rounded-full bg-foreground/95 px-3 py-1.5 text-xs font-medium text-background",
        "shadow-md backdrop-blur-sm",
        "transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
        hidden
          ? "-translate-x-1/2 -translate-y-2 opacity-0"
          : "-translate-x-1/2 translate-y-0 opacity-100",
      )}
      style={{
        // Tuned to clear the tallest sticky chrome we have today:
        //   - Project list: safe-top + 50px logo + 40px title row + padding
        //   - Project detail: safe-top + 56px ProjectHeader + ~52px RecordsToolbar
        // 8rem (128px) puts the chip ~18–30px under both, with no overlap.
        top: "calc(env(safe-area-inset-top, 0px) + 8rem)",
      }}
    >
      <CloudOff aria-hidden="true" className="size-3.5" />
      <span>Offline</span>
    </div>
  );
}
